/*
 * Sliding highlight for menus, tabs and switches.
 * When the selection moves, the highlight stretches: the edge in front travels first and fast,
 * the trailing edge catches up a moment later. One observer finds every menu on the page, so pages
 * need no extra code. Turns itself off for people who ask their device for reduced motion.
 */

type Mode = 'fill' | 'line';
interface Spec { box: string; item: string; mode: Mode }

const SPECS: Spec[] = [
  { box: '.side-scroll', item: '.side-a.on:not(.side-gh), .side-s.on', mode: 'fill' },
  { box: '.seg', item: ':scope > button.on', mode: 'fill' },
  { box: '.switch', item: ':scope > button.on', mode: 'fill' },
  { box: '.tp-chips', item: ':scope > button.on', mode: 'fill' },
  { box: '.lbx-sizes', item: ':scope > button.on', mode: 'fill' },
  { box: '.lbx-boards', item: ':scope > button.on', mode: 'fill' },
  { box: '.pts-tabs', item: ':scope > button.on, :scope > button[aria-selected="true"]', mode: 'fill' },
  { box: '.tp-tabs', item: ':scope > button.on', mode: 'line' },
  { box: '.tabs-u', item: ':scope > button.on', mode: 'line' },
];

interface Edges { l: number; t: number; r: number; b: number }
interface State { spec: Spec; pill: HTMLSpanElement; target: Element | null; at: Edges | null; raf: number; look: string; theme: string; dest: Edges | null }

const boxes = new Map<HTMLElement, State>();
const calm = () => typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;
const outCubic = (k: number) => 1 - Math.pow(1 - k, 3);
const inOutCubic = (k: number) => (k < 0.5 ? 4 * k * k * k : 1 - Math.pow(-2 * k + 2, 3) / 2);

function edgesOf(box: HTMLElement, el: Element, mode: Mode): Edges {
  const a = box.getBoundingClientRect(), r = el.getBoundingClientRect();
  const l = r.left - a.left + box.scrollLeft - box.clientLeft, t = r.top - a.top + box.scrollTop - box.clientTop;
  if (mode === 'line') { const inset = Math.min(10, r.width * 0.15); return { l: l + inset, r: l + r.width - inset, t: t + r.height - 2, b: t + r.height + 0 }; }
  return { l, t, r: l + r.width, b: t + r.height };
}

function paint(s: State, e: Edges) {
  s.at = e;
  const p = s.pill.style;
  p.transform = `translate(${e.l}px, ${e.t}px)`;
  p.width = `${Math.max(0, e.r - e.l)}px`;
  p.height = `${Math.max(0, e.b - e.t)}px`;
}

/** Copies how the selected item looks (colour, corners, shadow) onto the highlight. */
function dress(box: HTMLElement, s: State, el: Element) {
  box.classList.remove('pill-on');
  const cs = getComputedStyle(el);
  const bar = getComputedStyle(el, '::before');
  const look = [cs.backgroundColor, cs.borderRadius, cs.boxShadow, bar.content, bar.backgroundColor].join('|');
  if (look !== s.look) {
    s.look = look;
    const p = s.pill.style;
    if (s.spec.mode === 'line') {
      const after = getComputedStyle(el, '::after');
      p.background = after.backgroundColor && after.backgroundColor !== 'rgba(0, 0, 0, 0)' ? after.backgroundColor : 'var(--tp-a, #F2B705)';
      p.borderRadius = '2px';
    } else {
      p.background = cs.backgroundColor;
      p.borderRadius = cs.borderRadius;
      p.boxShadow = cs.boxShadow;
      s.pill.classList.toggle('pill-rail', bar.content !== 'none' && bar.content !== 'normal');
      if (bar.content !== 'none') s.pill.style.setProperty('--pill-bar', bar.backgroundColor);
    }
  }
  box.classList.add('pill-on');
}

function slide(s: State, to: Edges) {
  cancelAnimationFrame(s.raf);
  const from = s.at;
  s.dest = to;
  if (!from || calm()) { paint(s, to); return; }
  const vertical = Math.abs(to.t - from.t) > Math.abs(to.l - from.l);
  const fwd = vertical ? to.t > from.t : to.l > from.l;
  const [lead, trail] = (vertical ? (fwd ? ['b', 't'] : ['t', 'b']) : (fwd ? ['r', 'l'] : ['l', 'r'])) as [keyof Edges, keyof Edges];
  const cross: (keyof Edges)[] = vertical ? ['l', 'r'] : ['t', 'b'];
  const start = performance.now();
  const step = (now: number) => {
    const ms = now - start;
    const kl = outCubic(Math.min(1, ms / 360)), kt = inOutCubic(Math.min(1, ms / 550));
    const d = s.dest ?? to; // follows the item if the layout shifts mid-way
    const e = { ...from };
    e[lead] = from[lead] + (d[lead] - from[lead]) * kl;
    e[trail] = from[trail] + (d[trail] - from[trail]) * kt;
    for (const c of cross) e[c] = from[c] + (d[c] - from[c]) * kt;
    paint(s, e);
    if (ms < 550) s.raf = requestAnimationFrame(step); else { s.raf = 0; paint(s, d); }
  };
  s.raf = requestAnimationFrame(step);
}

function update(box: HTMLElement, s: State) {
  let el: Element | null = null;
  try { el = box.querySelector(s.spec.item); } catch { el = null; }
  if (!el || (el as HTMLElement).offsetParent === null) {
    s.pill.style.opacity = '0'; box.classList.remove('pill-on'); s.target = null; s.at = null; return;
  }
  const theme = document.documentElement.dataset.theme ?? '';
  if (el !== s.target || theme !== s.theme || !box.classList.contains('pill-on')) { s.theme = theme; dress(box, s, el); }
  s.pill.style.opacity = '1';
  const to = edgesOf(box, el, s.spec.mode);
  if (el !== s.target) { s.target = el; slide(s, to); return; }
  const d = s.dest;
  if (!d || Math.abs(d.l - to.l) + Math.abs(d.t - to.t) + Math.abs(d.r - to.r) + Math.abs(d.b - to.b) > 1) {
    s.dest = to;
    if (!s.raf || !s.at) paint(s, to);
  }
}

let queued = 0;
let observer: MutationObserver | null = null;
function scan() {
  queued = 0;
  for (const [box, s] of boxes) if (!box.isConnected) { cancelAnimationFrame(s.raf); boxes.delete(box); }
  for (const spec of SPECS) {
    document.querySelectorAll<HTMLElement>(spec.box).forEach((box) => {
      let s = boxes.get(box);
      if (!s || !s.pill.isConnected) {
        const pill = document.createElement('span');
        pill.className = `pill-x pill-${spec.mode}`;
        pill.setAttribute('aria-hidden', 'true');
        box.appendChild(pill);
        if (getComputedStyle(box).position === 'static') box.style.position = 'relative';
        s = { spec, pill, target: null, at: null, raf: 0, look: '', theme: '', dest: null };
        boxes.set(box, s);
      }
      update(box, s);
    });
  }
  // Changes made just now were our own (the highlight and its classes); don't react to them.
  observer?.takeRecords();
}
const later = () => { if (!queued) queued = requestAnimationFrame(scan); };

let started = false;
/** Turns on the sliding highlight for the whole app. Call once at start-up. */
export function startSlidingPills() {
  if (started || typeof document === 'undefined' || typeof MutationObserver === 'undefined') return;
  started = true;
  const mo = new MutationObserver(later);
  observer = mo;
  mo.observe(document.documentElement, { subtree: true, childList: true, attributes: true, attributeFilter: ['class', 'aria-selected', 'data-theme', 'aria-expanded'] });
  addEventListener('resize', later);
  document.fonts?.ready.then(later).catch(() => undefined);
  later();
}
